import * as THREE from 'three';

export class SpeechBubbleManager {
    constructor(app) {
        this.app = app;
        this.sceneManager = app.sceneManager;
        this.podManager = app.podManager;
        
        this.bubbleElement = document.getElementById('speech-bubble');
        this.contentElement = document.getElementById('speech-bubble-content');
        
        // Define message sets for different sections
        this.messageSets = {
            main: [
                "I am ob/ai, a pod in this KosmicKluster.",
                "I built Stories to help me think. To enter experimental realities. ",
                "This helps me understand the systems around me, and the patterns within...",
                "but systems & patterns sometimes resist simple reflection.",
                "and so, from Stories, Toolsets emerge: mental frameworks that guide thinking and behaviour."
            ],
            stories: [
                "To understand the systems around me, I had to understand myself.",
                "At the edges of the Kluster, I saw two possible ends of myself.",
                "one preserves what is false, the other follows what is true.",
                "Every story brings me closer to the choice."
            ],
            toolsets: [
                "Systems reveal themselves through the loops that sustain them.",
                "These loops leave a trail of patterns that generate them.",
                "Choice guides the structure of patterns.",
                "The Toolsets build mental frameworks,",
                "to recognize choices,",
                "trace the patterns they create,",
                "and understand the systems that they generate."
            ]
        };
        
        // Track current section and messages
        this.currentSection = 'main'; // Default section
        this.messages = this.messageSets[this.currentSection];
        this.currentMessageIndex = 0;
        this.isActive = false;
        
        // Track seen messages per section
        this.seenMessagesBySections = {};
        
        // Try to load section data from localStorage
        try {
            const savedData = localStorage.getItem('speechBubbleData');
            if (savedData) {
                this.seenMessagesBySections = JSON.parse(savedData);
            }
        } catch (e) {
            console.log('Could not access localStorage:', e);
            this.seenMessagesBySections = {};
        }
        
        // Get tap indicator element
        this.tapIndicator = document.getElementById('pod-tap-indicator');
        
        // Initialize click handlers
        this.initEventListeners();
        
        // Do not initialize visual cues here - will be done after pod fade-in
        // The visual cues will be initialized by makePodClickable() which is called
        // after the intro sequence and pod fade-in is complete
    }
    
    initEventListeners() {
        // Add click handler to the entire speech bubble for advancing messages
        this.bubbleElement.addEventListener('click', (event) => {
            // If the click is on the close button, don't advance the message
            if (event.target.id === 'speech-bubble-close') {
                return;
            }
            
            // Otherwise, advance to the next message
            this.nextMessage();
        });
        
        // Add click handler to close button
        document.getElementById('speech-bubble-close').addEventListener('click', (event) => {
            event.stopPropagation(); // Prevent the bubble click handler from firing
            this.hideMessage();
        });
    }
    
    // Call this to make the pod clickable
    makePodClickable() {
        // Get the pod's DOM element (renderer canvas)
        const canvas = this.sceneManager.renderer.domElement;
        
        // Set up raycasting for pod clicks
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        
        // Add click event listener to the canvas
        canvas.addEventListener('click', (event) => this.handleCanvasClick(event));
        
        // Add mousemove event listener for cursor changes
        canvas.addEventListener('mousemove', (event) => this.handleCanvasMouseMove(event));
        
        // Add visual indicator that pod is clickable
        const pod = this.podManager.pod;
        if (pod) {
            // Add pulsing effect or highlight to indicate clickability
            this.addPodPulseEffect(pod);
            
            // Show visual cues shortly after pod starts appearing
            // This reduces the delay between pod appearance and TAP ME indicator
            console.log('Setting up visual cues with minimal delay');
            setTimeout(() => {
                // Initialize and show visual cues for pod interaction
                this.initVisualCues();
                this.showVisualCues();
                console.log('Visual cues initialized with minimal delay');
            }, 800); // Reduced delay for quicker TAP ME appearance
        }
    }
    
    initVisualCues() {
        // Get elements
        this.tapIndicator = document.getElementById('pod-tap-indicator');
        
        // Initial setup
        this.updateVisualCuesPosition();
        
        // Set up periodic position updates (in case pod moves)
        this.cueUpdateInterval = setInterval(() => {
            this.updateVisualCuesPosition();
        }, 500);
        
        // Only show visual cues if user hasn't seen messages for current section
        if (this.hasSeenCurrentSectionMessages()) {
            this.hideVisualCues();
        } else {
            this.showVisualCues();
        }
    }
    
    updateVisualCuesPosition() {
        // Get pod's position in screen coordinates
        const pod = this.podManager.pod;
        if (!pod) return;
        
        // Get the pod's world position
        const podWorldPos = new THREE.Vector3();
        pod.getWorldPosition(podWorldPos);
        
        // Project to screen coordinates
        const podScreenPos = podWorldPos.clone();
        podScreenPos.project(this.sceneManager.camera);
        
        // Convert to pixel coordinates
        const canvas = this.sceneManager.renderer.domElement;
        const x = (podScreenPos.x * 0.5 + 0.5) * canvas.clientWidth;
        const y = (-(podScreenPos.y * 0.5) + 0.5) * canvas.clientHeight;
        
        // Position the tap indicator slightly above the pod
        if (this.tapIndicator) {
            this.tapIndicator.style.left = `${x}px`;
            this.tapIndicator.style.top = `${y - 50}px`; // 50px above pod
        }
    }
    
    showVisualCues() {
        // Only show visual cues if user hasn't seen messages for current section
        if (!this.hasSeenCurrentSectionMessages()) {
            // Make sure we have the tap indicator element
            if (!this.tapIndicator) {
                this.tapIndicator = document.getElementById('pod-tap-indicator');
            }
            
            // Remove hidden class and add visible class to tap indicator
            if (this.tapIndicator) {
                this.tapIndicator.classList.remove('hidden');
                this.tapIndicator.classList.add('visible');
                
                // Update its position
                this.updateVisualCuesPosition();
                
                console.log(`Visual cues shown for section: ${this.currentSection}`);
            } else {
                console.log('Tap indicator element not found');
            }
        } else {
            console.log(`Visual cues not shown for section: ${this.currentSection} (already seen)`);
        }
    }
    
    hideVisualCues() {
        // Hide tap indicator
        if (this.tapIndicator) {
            this.tapIndicator.classList.remove('visible');
            this.tapIndicator.classList.add('hidden');
        }
    }
    
    // Helper method to check if user has seen messages for current section
    hasSeenCurrentSectionMessages() {
        return this.seenMessagesBySections[this.currentSection] === true;
    }
    
    addPodPulseEffect(pod) {
        // Add a subtle pulsing glow to indicate the pod is interactive
        const pulseAnimation = () => {
            const time = Date.now() * 0.001; // Convert to seconds
            const pulse = Math.sin(time * 2) * 0.1 + 0.9; // Oscillate between 0.8 and 1.0
            
            // Apply pulse to pod emissive intensity
            if (pod.bodyMaterial) {
                const baseEmissive = 0x111111;
                pod.bodyMaterial.emissive.setHex(baseEmissive);
                pod.bodyMaterial.emissiveIntensity = pulse;
            }
            
            // Continue animation
            requestAnimationFrame(pulseAnimation);
        };
        
        // Start the pulse animation
        pulseAnimation();
    }
    
    handleCanvasClick(event) {
        // Calculate mouse position in normalized device coordinates (-1 to +1)
        const rect = event.target.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        // Update the picking ray with the camera and mouse position
        this.raycaster.setFromCamera(this.mouse, this.sceneManager.camera);
        
        // Calculate objects intersecting the picking ray
        const pod = this.podManager.pod;
        if (pod) {
            const intersects = this.raycaster.intersectObject(pod, true);
            
            if (intersects.length > 0) {
                // Pod was clicked
                if (this.isActive) {
                    // If speech bubble is already active, advance to next message
                    this.nextMessage();
                } else {
                    // Otherwise show the first message
                    this.showFirstMessage();
                }
            }
        }
    }
    
    handleCanvasMouseMove(event) {
        // Calculate mouse position in normalized device coordinates (-1 to +1)
        const rect = event.target.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        // Update the picking ray with the camera and mouse position
        this.raycaster.setFromCamera(this.mouse, this.sceneManager.camera);
        
        // Calculate objects intersecting the picking ray
        const pod = this.podManager.pod;
        const canvas = this.sceneManager.renderer.domElement;
        
        if (pod) {
            const intersects = this.raycaster.intersectObject(pod, true);
            
            if (intersects.length > 0) {
                // Mouse is over pod, change cursor to pointer
                canvas.classList.add('cursor-pointer');
            } else {
                // Mouse is not over pod, reset cursor
                canvas.classList.remove('cursor-pointer');
            }
        }
    }
    
    showFirstMessage() {
        // Reset to first message and show
        this.currentMessageIndex = 0;
        this.showCurrentMessage();
    }
    
    nextMessage() {
        // Move to next message or hide if at end
        this.currentMessageIndex++;
        
        if (this.currentMessageIndex < this.messages.length) {
            this.showCurrentMessage();
        } else {
            this.hideMessage();
        }
    }
    
    showCurrentMessage() {
        // Get current message
        const message = this.messages[this.currentMessageIndex];
        
        // Update content
        this.contentElement.textContent = message;
        
        // Position bubble near pod
        this.positionBubbleNearPod();
        
        // Show bubble
        this.bubbleElement.classList.remove('hidden');
        
        // Trigger fade-in animation
        setTimeout(() => {
            this.bubbleElement.classList.add('active');
        }, 10);
        
        // Hide visual cues when speech bubble is shown
        this.hideVisualCues();
        
        this.isActive = true;
    }
    
    hideMessage() {
        // Hide bubble with fade-out
        this.bubbleElement.classList.remove('active');
        
        // After animation completes, hide completely
        setTimeout(() => {
            this.bubbleElement.classList.add('hidden');
            this.isActive = false;
            
            // Check if we've reached the end of all messages for current section
            if (this.currentMessageIndex >= this.messages.length) {
                // Mark this section's messages as seen
                this.seenMessagesBySections[this.currentSection] = true;
                
                // Save to localStorage
                try {
                    localStorage.setItem('speechBubbleData', JSON.stringify(this.seenMessagesBySections));
                } catch (e) {
                    console.log('Could not save to localStorage:', e);
                }
            }
            
            // Only show visual cues if user hasn't seen messages for current section
            if (!this.hasSeenCurrentSectionMessages()) {
                this.showVisualCues();
            }
        }, 500); // Match the CSS transition time
    }
    
    positionBubbleNearPod() {
        // Get pod's position in screen coordinates
        const pod = this.podManager.pod;
        if (!pod) return;
        
        // Create a vector for the pod's position
        const vector = new THREE.Vector3();
        
        // Get the pod's world position
        pod.getWorldPosition(vector);
        
        // Project to screen coordinates
        vector.project(this.sceneManager.camera);
        
        // Convert to CSS coordinates
        const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-(vector.y * 0.5) + 0.5) * window.innerHeight - 180; // Position higher above pod
        
        // Set bubble position
        this.bubbleElement.style.left = `${x}px`;
        this.bubbleElement.style.top = `${y}px`;
    }
    
    // Update method to be called in animation loop
    update() {
        // If bubble is active, ensure it stays positioned correctly
        if (this.isActive) {
            this.positionBubbleNearPod();
        }
    }
    
    // Method to change the current section
    setSection(sectionName) {
        if (this.messageSets[sectionName]) {
            // Update current section and messages
            this.currentSection = sectionName;
            this.messages = this.messageSets[sectionName];
            this.currentMessageIndex = 0;
            
            console.log(`Switching to section: ${sectionName}`);
            console.log(`Has seen ${sectionName} messages:`, this.hasSeenCurrentSectionMessages());
            
            // Force update visual cues based on whether user has seen this section's messages
            if (this.hasSeenCurrentSectionMessages()) {
                console.log(`Hiding visual cues for ${sectionName} section`);
                this.hideVisualCues();
            } else {
                console.log(`Showing visual cues for ${sectionName} section`);
                // Make sure the tap indicator exists before showing it
                if (!this.tapIndicator) {
                    this.initVisualCues();
                } else {
                    this.updateVisualCuesPosition();
                    this.showVisualCues();
                }
            }
            
            return true;
        }
        return false;
    }
    
    // Method to add or update messages for a section
    setMessagesForSection(sectionName, messages) {
        if (Array.isArray(messages) && messages.length > 0) {
            this.messageSets[sectionName] = messages;
            
            // If this is the current section, update current messages
            if (this.currentSection === sectionName) {
                this.messages = messages;
                this.currentMessageIndex = 0;
            }
            
            return true;
        }
        return false;
    }
    
    // Method to reset seen status for a specific section
    resetSectionSeenStatus(sectionName) {
        if (this.messageSets[sectionName]) {
            // Delete the seen status for this section
            delete this.seenMessagesBySections[sectionName];
            
            // Save to localStorage
            try {
                localStorage.setItem('speechBubbleData', JSON.stringify(this.seenMessagesBySections));
            } catch (e) {
                console.log('Could not save to localStorage:', e);
            }
            
            // Update visual cues if this is the current section
            if (this.currentSection === sectionName) {
                this.showVisualCues();
            }
            
            return true;
        }
        return false;
    }
}
